import 'rxjs/add/operator/map';
import 'rxjs/Rx';

import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { AppMessage } from '../../Common/appMessage';
import { Utility } from '../../Common/Services/utility';
import { AppConfig } from './../../Common/appConfiguration';
import { AdmManageConcurrentExamsService } from './adm-manage-concurrent-exams.service';
import { SharedService } from "./../../Common/Services/shared.service";
import { Enum } from "../../utility/enum.enum";
import { examTypeOptions } from "./../../Common/appConstant";
import { FeatureFlagService, ToggleName } from 'app/Common/Services/feature-flag.service';

class CapacityGroup {
  formGroups: any;
  formsError: string = "";
  timeZone: string = "UTC";
  formsInvalid: boolean = false;
  editBtnDisabled: boolean = false;
  updateBtnDisabled: boolean = true;
  addBtnDisabled: boolean = true;
  groupType: string;

  defaultStartDate: Date = new Date("2000-01-01");
  defaultEndDate: Date = new Date("3000-01-01");

  utility: Utility;
  capacityService: AdmManageConcurrentExamsService;
  appMessage: AppMessage;

  constructor(groupType, capacityService, utility, appMessage) {
    this.groupType = groupType;
    this.capacityService = capacityService;
    this.utility = utility;
    this.appMessage = appMessage;
  }

  isDefault() {
    return this.groupType == "default";
  }

  getExamTypeDisplay(formGroup?: any) {
    let examType = this.groupType;

    if (formGroup) {
      examType = formGroup.getRawValue().examType;
    }

    for (let i = 0; i < examTypeOptions.length; i ++) {
      if (examTypeOptions[i].examType == examType) {
        // TODO: This is/was a hack for the combining of testing center and remote
        // exam types
        if (examTypeOptions[i].examType == "CE" || examTypeOptions[i].examType == "RE") {
          return "Testing Center / Remote";
        } else {
          return "Preliminary / Experience";
        }
      }
    }
    return "";
  }

  compareCapacityConfigs(a, b) {
    // NOTE: validateForms is dependant on the order defined here.

    // Compare start time
    if (a.startTime > b.startTime) return 1;
    if (a.startTime < b.startTime) return -1;

    // Compare end time
    if (a.endTime > b.endTime) return 1;
    if (a.endTime < b.endTime) return -1;

    // Compare start date (startDate undefined == has always been)
    if (a.startDate && !b.startDate) return 1;
    if (!a.startDate && b.startDate) return -1;
    if (a.startDate && b.startDate) {
      if (new Date(a.startDate) > new Date(b.startDate)) return 1;
      if (new Date(a.startDate) < new Date(b.startDate)) return -1;
    }

    // Compare end date (endDate undefined == allways will be)
    if (!a.endDate && b.endDate) return 1;
    if (a.endDate && !b.endDate) return -1;
    if (a.endDate && b.endDate) {
      if (new Date(a.endDate) > new Date(b.endDate)) return 1;
      if (new Date(a.endDate) < new Date(b.endDate)) return -1;
    }

    // else:
    // times are the same
    // dates are the same
    // dates aren't defined
    return 0;
  }

  compareCapacityFormGroups(a, b) {
    return this.compareCapacityConfigs(a.getRawValue(), b.getRawValue());
  }

  normalizeDate(date, usedFor) {
    if (date) return new Date(date);
    if (usedFor == "start") return this.defaultStartDate;
    if (usedFor == "end") return this.defaultEndDate;
    return null;
  }

  addDay(date) {
    if (date) return new Date(date.getTime() + 86400000);
    return null;
  }

  configDatesOverlaps(a, b) {
    return (a.startDate >= b.startDate && a.startDate <= b.endDate)
        || (a.endDate >= b.startDate && a.endDate <= b.endDate)
        || (a.startDate <= b.startDate && a.endDate >= b.endDate);
  }

  validateForms() {
    this.formsInvalid = this.formGroups.map(fg => fg.invalid).some(x => x);

    // check to make sure the times don't overlap
    if (!this.formsInvalid) {
      let formData = this.formGroups.map(form => form.getRawValue());

      // normalize data for validation (not for saving)
      let normFormData = [];
      for (let i = 0; i < formData.length; i++) {
        let c = formData[i];
        if (c.startTime > c.endTime) {
          // split the capacity config to keep it within a day
          normFormData.push({
            startTime: c.startTime,
            endTime: '24:00:00',
            startDate: this.normalizeDate(c.startDate, "start"),
            endDate: this.normalizeDate(c.endDate, "end"),
          });
          normFormData.push({
            startTime: '00:00:00',
            endTime: c.endTime,
            startDate: this.addDay(this.normalizeDate(c.startDate, "start")),
            endDate: this.addDay(this.normalizeDate(c.endDate, "end")) // spans a day
          });
        } else {
          normFormData.push({
            startTime: c.startTime,
            endTime: c.endTime,
            startDate: this.normalizeDate(c.startDate, "start"),
            endDate: this.normalizeDate(c.endDate, "end")
          });
        }
      }

      // The checks below depend on the order defined here
      normFormData.sort(this.compareCapacityConfigs);

      for (let i = 0; i < normFormData.length; i++) {
        let c = normFormData[i];
        if (c.startTime == c.endTime && c.endTime != "00:00:00") {
          this.formsInvalid = true;
          this.formsError = "Start and end times should be different";
          break;
        }

        if (c.startDate > c.endDate) {
          this.formsInvalid = true;
          this.formsError = "Start date should not be greater than the end date";
          break;
        }

        let next = normFormData[i + 1];
        if (next && c.endTime > next.startTime && this.configDatesOverlaps(c, next)) {
          this.formsInvalid = true;
          this.formsError = "Schedules should not overlap";
          break;
        }
      }
    }
  }

  createFormGroup(config, disabled) {
    let formGroup = new FormGroup({
      examType: new FormControl(config.examType, [Validators.required]),
      capacity: new FormControl(config.capacity || 0, [Validators.required, Validators.pattern(AppConfig.onlyNumericRegEx)]),
      startDate: new FormControl(config.startDate),
      endDate: new FormControl(config.endDate),
      startTime: new FormControl(config.startTime || "00:00:00", [Validators.required]),
      endTime: new FormControl(config.endTime || "00:00:00", [Validators.required])
    });

    this.utility.disableEnableForm(formGroup, disabled);
    formGroup.valueChanges.subscribe(this.validateForms.bind(this));
    return formGroup;
  }

  loadCapacityConfigs() {
    this.addBtnDisabled = true;
    this.editBtnDisabled = false;
    this.updateBtnDisabled = true;
    this.utility.addLoaderCount();
    this.capacityService.getCapacityConfigs(this.timeZone, this.groupType).subscribe((response: any) => {
      this.utility.onSuccessCallback(response, null, () => {
        if (response && response.response) {
          this.formGroups = response.response.results.map(config => {
            return this.createFormGroup(config, true);
          });
        }
        this.validateForms();
      });
    }, error => { this.utility.onErrorCallback(error); });
  }

  onAdd() {
    if (!this.isDefault()) {
      this.formGroups.push(this.createFormGroup({"examType": this.groupType}, false));
      this.validateForms();
    }
  }

  onEdit() {
    this.formGroups.forEach(form => {
      this.utility.disableEnableForm(form, false);
    });
    this.addBtnDisabled = false;
    this.editBtnDisabled = true;
    this.updateBtnDisabled = false;
  }

  onSort() {
    this.formGroups.sort(this.compareCapacityFormGroups.bind(this));
  }

  onUpdate(): void {
    let formData = this.formGroups.map(form => form.getRawValue());
    formData.sort(this.compareCapacityConfigs);
    formData.forEach((d) => d['timeZone'] = this.timeZone);

    this.capacityService.updateCapacityConfigs(this.groupType, formData)
      .subscribe((data: any) => {
        let responseDescription = data.responseCode.startsWith('E') ? data.responseDescription : this.appMessage.getItem['UMsg00093'];
        this.utility.onSuccessCallback(data, responseDescription, () => {
          this.utility.clearTxnStatus();
          this.loadCapacityConfigs();
        });
      }, error => this.utility.onErrorCallback(error));
  }

  onDelete(formGroup) {
    let i = this.formGroups.indexOf(formGroup);
    if (i > -1) this.formGroups.splice(i, 1);
    this.validateForms();
  }

  updateTimeZone(timeZone: string) {
    if (!this.isDefault()) {
      this.timeZone = timeZone;
      this.loadCapacityConfigs();
    }
  }
}

@Component({
  selector: 'app-adm-manage-concurrent-exams',
  templateUrl: './adm-manage-concurrent-exams.component.html',
  styleUrls: ['./adm-manage-concurrent-exams.component.css'],
  providers: [AdmManageConcurrentExamsService, FeatureFlagService]
})
export class AdmManageConcurrentExamsComponent implements OnInit, OnDestroy {
  capacityGroups: any;
  scheduleTimeZoneForm: FormGroup;
  timeZoneFilteredOptions: any;
  timeZoneToEditIn: string = "UTC";
  // Time choices
  // 00:00:00, 00:30:00, 01:00:00, ..., 23:30:00
  capacityTimes: any = Array.from(Array(24).keys()).reduce((a, i) => {
    let t = i < 10 ? "0" + i : i.toString();
    a.push(t + ":00:00");
    a.push(t + ":15:00");
    a.push(t + ":30:00");
    a.push(t + ":45:00");
    return a
  }, []);

  constructor(public _utility: Utility,
    private featureFlags: FeatureFlagService,
    private _admManageConcurrentExamsService: AdmManageConcurrentExamsService,
    private _appMessage: AppMessage,
    private _sharedService: SharedService) {
    this.scheduleTimeZoneForm = new FormGroup({
      timeZoneControl: new FormControl("")
    });
  }

  private redirect(): void {
    window.location.assign("/ies/internal/concurrent-exams");
  }

  ngOnInit() {
    this.featureFlags.on("ready", () => {
      if (this.featureFlags.isEnabled(ToggleName.ENABLE_CONCURRENT_EXAMS_REDIRECT)) {
        this.redirect();
      }
    });
    this.featureFlags.on("update", () => {
      if (this.featureFlags.isEnabled(ToggleName.ENABLE_CONCURRENT_EXAMS_REDIRECT)) {
        this.redirect();
      }
    });
    this.featureFlags.start();
    this.capacityGroups = [
      new CapacityGroup(
        "default", this._admManageConcurrentExamsService, this._utility, this._appMessage
      )
    ];

    examTypeOptions.forEach((opt) => {
      if (opt.validForCapacity) {
        this.capacityGroups.push(new CapacityGroup(
          opt.examType, this._admManageConcurrentExamsService, this._utility, this._appMessage
        ));
      }
    });

    this.capacityGroups.forEach((group) => {
      group.loadCapacityConfigs();
    });

    // get timeZone data and bind with AutoComplete
    if (this._sharedService.timeZoneList) {
      this._utility.setDataInComboField("timeZone", null, this.scheduleTimeZoneForm.controls.timeZoneControl, this);
    } else {
      this._utility.addLoaderCount();
      this._sharedService.getComboFieldsData(Enum.TimeZone, (response) => {
        this._utility.removeLoaderCount();
        this._sharedService.timeZoneList = response.response;
        this._utility.setDataInComboField("timeZone", null, this.scheduleTimeZoneForm.controls.timeZoneControl, this);
      });
    }
  }

  ngOnDestroy(): void {
      this.featureFlags.stop();
  }

  onUpdateTimeZone() {
    let formValues = this.scheduleTimeZoneForm.getRawValue();
    if (formValues.timeZoneControl.name) {
      this.timeZoneToEditIn = formValues.timeZoneControl.name || "UTC";
    } else {
      this.timeZoneToEditIn = "UTC";
    }

    this.capacityGroups.forEach((group) => {
      group.updateTimeZone(this.timeZoneToEditIn);
    });
  }
}




// WEBPACK FOOTER //
// /opt/app-root/src/src/app/Components/adm-manage-concurrent-exams/adm-manage-concurrent-exams.component.ts